import React, { useState, useEffect, useRef } from 'react';
import { string, object, bool, func, array } from 'prop-types';
import cn from 'classnames';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import FacetPanelLoading from 'ps-components/FacetPanel/FacetPanelLoading/FacetPanelLoading';
import Search from 'ps-components/Search/Search';
import ReactTooltip from 'react-tooltip';
import addAlert from 'ps-components/AlertToast/addAlert';

import './ElementSelection.scss';
import { Overlay } from '@mantine/core';

ElementSelection.propTypes = {
  pageCode: string,
  categoryCode: string,
  pageName: string,
  tree: object,
  treeLoading: bool,
  toggleSelectedElement: func,
  selectedElements: array,
  isReadOnly: bool,
  treeLoaded: bool,
  elementsFilter: func,
  categoryFilter: func
};

export default function ElementSelection(props) {
  const [search, setSearch] = useState('');
  const [showSelectedElements, setShowSelectedElements] = useState(false);

  // ref for updating internal state of search input (faster on IE)
  const searchInputRef = useRef();
  // ref for scrolling up after search
  const scrollRef = useRef();

  function executeSearch(value) {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset pagination scroll
    }
    setSearch(value);
  }

  // clicking x to clear search text
  function searchOnClick() {
    executeSearch('');
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      executeSearch(event.target.value);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearch(value) {
    executeSearch(value);
  }

  function getTree() {
    const tree = [];

    props.tree?.forEach((cat, idx, arr) => {
      const category = cat?.children?.reduce((result, section) => {
        const sectionElements = getSectionElements(section?.children, section?.text);

        if (sectionElements) {
          result.push(
            <ElementSection
              {...section}
              key={`section-content-${section.text}`}
              showSelectedElements={showSelectedElements}
              toggle={!!search}
            >
              {sectionElements}
            </ElementSection>
          );
        }

        return result;
      }, []);

      let showCategory = true;
      if (props.categoryFilter) showCategory = props.categoryFilter(cat, idx, arr);

      if (category.length > 0 && showCategory) {
        tree.push(
          <ElementCategory
            {...cat}
            key={`category-content-${cat.text}`}
            showSelectedElements={showSelectedElements}
            toggle={!!search}
          >
            <div className="tree-category__content">{category}</div>
          </ElementCategory>
        );
      }
    });

    return tree;
  }

  function replaceSectionName(value, name) {
    return value === name ? value : value?.replaceAll(name, '');
  }

  function isSelected(element) {
    return props.selectedElements.findIndex(e => e.field_pk === element.field_pk) > -1;
  }

  function isMandatory(element) {
    return props.selectedElements.find(e => e.field_pk === element.field_pk)?.mandatory_field === true;
  }

  function onClickElement(element) {
    if (!props.isReadOnly) {
      props.toggleSelectedElement(element);
    }
  }

  function getSectionElements(elements, sectionName) {
    const monetaryFields = [];
    const nonMonFields = [];

    elements?.forEach((element, index, arr) => {
      const selected = isSelected(element);
      const mandatory = isMandatory(element);
      const elementName = replaceSectionName(element.text, sectionName);
      let includeElement = element.text.toLowerCase().includes(search.toLowerCase());
      const keyName = `element-selection-${sectionName}-${elementName}-${index}`;

      if (showSelectedElements && !selected) {
        includeElement = false;
      }

      if (props.elementsFilter) includeElement = props.elementsFilter(element, index, arr);

      if (includeElement) {
        if (element.field_type_code === 'monetary') {
          monetaryFields.push(
            <>
              <div
                key={`mon-${keyName}`}
                className={cn('monetary', { selected })}
                onClick={() => onClickElement(element)}
              >
                {elementName}
                {element.qtip && (
                  <>
                    <span className="icon-reverse icon-circle data-element__formula-icon">
                      <i
                        className="icon-info"
                        data-tip={element.qtip}
                        data-html={true}
                        data-row-key={`tooltip-${keyName}`}
                        key={`tooltip-${keyName}`}
                      />
                    </span>
                  </>
                )}
              </div>

              <ReactTooltip place="bottom" />
            </>
          );
        } else {
          nonMonFields.push(
            <div
              key={`non-mon-${keyName}`}
              className="non-monetary"
              onClick={() => (mandatory ? null : onClickElement(element))}
            >
              <CheckboxInput className="non-monetary__checkbox" checked={selected} disabled={mandatory} />

              <span className="non-monetary__text" key={keyName}>
                {elementName}
                {element.qtip && (
                  <>
                    <span className="icon-reverse icon-circle data-element__formula-icon">
                      <i className="icon-info" data-tip={element.qtip} data-html={true} />
                    </span>

                    <ReactTooltip data-row-key={`tooltip-${keyName}`} key={`tooltip-${keyName}`} place="bottom" />
                  </>
                )}
              </span>
            </div>
          );
        }
      }
    });

    if (monetaryFields.length > 0 || nonMonFields.length > 0) {
      return (
        <div className="element-container">
          {monetaryFields.length > 0 && (
            <>
              <div className="monetary-title">Monetary Fields</div>
              <div className="field-container__monetary">{monetaryFields}</div>
            </>
          )}

          {nonMonFields.length > 0 && <div className="field-container__non-monetary">{nonMonFields}</div>}
        </div>
      );
    }

    return null;
  }

  function getEmptyPlaceholder() {
    if (props.treeLoading || !props.treeLoaded) {
      return (
        <FacetPanelLoading
          className="element-selection__loading"
          numberOfFacets={3}
          numberOfSubItems={5}
          includeHeader={false}
        />
      );
    }

    return (
      <div className="element-selection__empty">
        No elements found for <br />
        {props.pageName}
      </div>
    );
  }

  return (
    <div className="configure-views__element-selection">
      {!props.tree || props.tree.length === 0 || props.treeLoading ? (
        getEmptyPlaceholder()
      ) : (
        <>
          <div
            className={cn('element-selection__toggle', 'left', { active: !showSelectedElements })}
            onClick={() => setShowSelectedElements(false)}
          >
            All
          </div>
          <div
            className={cn('element-selection__toggle', 'right', { active: showSelectedElements })}
            onClick={() => setShowSelectedElements(true)}
          >
            Selected
          </div>

          <Search
            className="element-selection__search"
            onClick={searchOnClick}
            onKeyDown={searchOnKeyDown}
            onSearch={searchOnSearch}
            useInternalState
            ref={searchInputRef}
          />
          <div className="element-selection__facets facets" ref={scrollRef}>
            {getTree()}
          </div>
        </>
      )}
    </div>
  );
}

function ElementCategory(props) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (props.showSelectedElements) setIsOpen(true);
  }, [props.showSelectedElements]);

  useEffect(() => {
    if (props.toggle) {
      setIsOpen(props.toggle);
    }
  }, [props.toggle]);

  return (
    <div className="tree-category" key={`category-${props.text}`}>
      <div className="tree-category__top" onClick={() => setIsOpen(!isOpen)}>
        <div className="tree-category__name">{props.text}</div>
        <div className={cn('tree-category__arrow facets__facet__caret', { open: isOpen })} />
      </div>

      {isOpen && props.children}
    </div>
  );
}

function ElementSection(props) {
  const [isOpen, setIsOpen] = useState(props.showSelectedElements || false);

  useEffect(() => {
    if (props.showSelectedElements) setIsOpen(true);
  }, [props.showSelectedElements]);

  useEffect(() => {
    if (props.toggle) {
      setIsOpen(props.toggle);
    }
  }, [props.toggle]);

  return (
    <div className="tree-section" key={`section-${props.text}`}>
      <div className="tree-section__top" onClick={() => setIsOpen(!isOpen)}>
        <div className={cn('tree-section__arrow', { open: isOpen })} />
        <div className="tree-section__name">{props.text}</div>
      </div>

      {isOpen && props.children}
    </div>
  );
}
